import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HeaderContext } from '../contexts/HeaderContext';
import { getProfilePicture } from '@lf/api-client';
function Header({ userProfile, subscriptionAction, onUserProfile, onChildProfile, onAddChildProfile }) {
    const location = useLocation();
    const { headerData } = useContext(HeaderContext);
    const isIndexPage = location.pathname === '/www_v1';
    const username = location.pathname.split('/')[1] === 'profile' ? location.pathname.split('/')[2] : '';
    const [shouldAnimateBubbles, setShouldAnimateBubbles] = useState(false);
    const bubbleRefs = useRef([]);
    const wasLoggedInRef = useRef(Boolean(userProfile));

    useEffect(() => {
        const isLoggedIn = Boolean(userProfile);
        const wasLoggedIn = wasLoggedInRef.current;

        if (isLoggedIn && !wasLoggedIn) {
            setShouldAnimateBubbles(true);
        }
        if (!isLoggedIn) {
            setShouldAnimateBubbles(false);
        }

        wasLoggedInRef.current = isLoggedIn;
    }, [userProfile]);

    useEffect(() => {
        if (shouldAnimateBubbles) return;
        bubbleRefs.current.forEach((element) => {
            if (!element) return;
            element.style.opacity = '1';
            element.style.transform = 'translate3d(0, 0px, 0)';
        });
    }, [shouldAnimateBubbles]);

    const bubbleColors = [
        'bg-pastelblue/70',
        'bg-pastelyellow/70',
        'bg-pastelgreen/70',
        'bg-pastelpurple/70',
    ];

    function getStyle(childUsername) {
        return childUsername === username ? 'ring-blue-600' : 'ring-transparent';
    }

    function hasSubscriptionAction() {
        return subscriptionAction.action !== 'none';
    }

    const bubbleAnimationConfigs = useMemo(() => {
        const childCount = headerData?.length || 0;
        const includeAddBubble = subscriptionAction.action === 'none';
        const bubbleCount = childCount + (includeAddBubble ? 1 : 0);

        return Array.from({ length: bubbleCount }, () => {
            const delayMs = Math.floor(Math.random() * 1000);
            const durationMs = 3300 + Math.floor(Math.random() * 1001);
            const gravity = 1550 + Math.random() * 760;
            const restitution = 0.38 + Math.random() * 0.28;
            const bounceDamping = 0.88 + Math.random() * 0.1;
            const fallDistanceRatio = 0.7 + Math.random() * 0.26;

            return {
                delayMs,
                durationMs,
                gravity,
                restitution,
                bounceDamping,
                fallDistanceRatio,
            };
        });
    }, [headerData, subscriptionAction.action]);

    useEffect(() => {
        bubbleRefs.current = bubbleRefs.current.slice(0, bubbleAnimationConfigs.length);
    }, [bubbleAnimationConfigs.length]);

    const canAnimateBubbles = !isIndexPage && Boolean(username) && bubbleAnimationConfigs.length > 0;

    useEffect(() => {
        if (!shouldAnimateBubbles || !canAnimateBubbles) return;
        const timer = setTimeout(() => setShouldAnimateBubbles(false), 6200);
        return () => clearTimeout(timer);
    }, [shouldAnimateBubbles, canAnimateBubbles]);

    useEffect(() => {
        if (!shouldAnimateBubbles || !canAnimateBubbles) return;

        let animationFrameId;
        const startTime = performance.now();
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;

        const simulations = bubbleAnimationConfigs
            .map((config, index) => {
                const element = bubbleRefs.current[index];
                if (!element) return null;

                const sim = {
                    element,
                    delayMs: config.delayMs,
                    durationMs: config.durationMs,
                    gravity: config.gravity,
                    restitution: config.restitution,
                    bounceDamping: config.bounceDamping,
                    y: -Math.max(280, Math.round(viewportHeight * config.fallDistanceRatio)),
                    vy: 0,
                    bounceCount: 0,
                    lastTime: null,
                    done: false,
                };

                sim.element.style.opacity = '0';
                sim.element.style.transform = `translate3d(0, ${sim.y}px, 0)`;
                return sim;
            })
            .filter(Boolean);

        if (!simulations.length) return;

        const tick = (now) => {
            let allDone = true;

            simulations.forEach((sim) => {
                if (sim.done) return;
                allDone = false;

                const elapsed = now - startTime;
                if (elapsed < sim.delayMs) return;

                const localElapsed = elapsed - sim.delayMs;
                if (localElapsed >= sim.durationMs) {
                    sim.done = true;
                    sim.element.style.opacity = '1';
                    sim.element.style.transform = 'translate3d(0, 0px, 0)';
                    return;
                }

                const rawDt = sim.lastTime ? Math.min((now - sim.lastTime) / 1000, 0.04) : 0;
                sim.lastTime = now;

                if (rawDt > 0) {
                    const substep = 1 / 120;
                    const steps = Math.max(1, Math.ceil(rawDt / substep));
                    const dt = rawDt / steps;

                    for (let i = 0; i < steps; i += 1) {
                        sim.vy += sim.gravity * dt;
                        sim.y += sim.vy * dt;

                        if (sim.y > 0) {
                            sim.y = 0;
                            sim.vy = -Math.abs(sim.vy) * sim.restitution * Math.pow(sim.bounceDamping, sim.bounceCount);
                            sim.bounceCount += 1;
                        }
                    }
                }

                if (localElapsed > sim.durationMs * 0.62 && Math.abs(sim.vy) < 22 && Math.abs(sim.y) < 0.9) {
                    sim.done = true;
                    sim.element.style.opacity = '1';
                    sim.element.style.transform = 'translate3d(0, 0px, 0)';
                    return;
                }

                const fadeProgress = Math.min(1, localElapsed / 180);
                sim.element.style.opacity = fadeProgress.toFixed(3);
                sim.element.style.transform = `translate3d(0, ${Math.min(0, sim.y).toFixed(2)}px, 0)`;
            });

            if (!allDone) {
                animationFrameId = requestAnimationFrame(tick);
            }
        };

        animationFrameId = requestAnimationFrame(tick);
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            simulations.forEach((sim) => {
                sim.element.style.opacity = '1';
                sim.element.style.transform = 'translate3d(0, 0px, 0)';
            });
        };
    }, [shouldAnimateBubbles, canAnimateBubbles, bubbleAnimationConfigs]);

    return <nav className="bg-sky pt-4">
        <div className="flex justify-between mx-auto max-w-screen-lg">
            {/* Left-aligned content */}
            <div>
            </div>

            {/* Right-aligned div */}
            <div className="ml-auto flex flex-col">
                <div className="mr-4 cursor-pointer" onClick={onUserProfile}>
                    { userProfile ? userProfile.name : '' }
                </div>
            </div>
        </div>
        {isIndexPage ? (
            <div className="relative">
                <img
                    src="/bg-sky-top-left.jpeg"
                    alt=""
                    className="absolute bottom-0 left-0 w-1/2 sm:w-1/3 lg:w-1/4 pointer-events-none select-none"
                />
                <img
                    src="/bg-sky-top-right.jpeg"
                    alt=""
                    className="absolute bottom-0 right-0 w-1/2 sm:w-1/3 lg:w-1/4 pointer-events-none select-none"
                />
                <div className="relative z-10 max-w-screen-lg mx-auto px-6 py-10 sm:py-16 text-center">
                    <h1
                        className="text-3xl sm:text-5xl py-4 font-bold text-darkrock leading-tight"
                        style={{
                            fontFamily: '"Fraunces", "Manrope", serif',
                            fontOpticalSizing: 'auto',
                            textWrap: 'balance',
                        }}
                    >
                        Distraction-free video learning for the YouTube generation
                    </h1>
                    <p
                        className="mt-4 text-base sm:text-lg text-darkrock/70 max-w-xl mx-auto"
                        style={{ textWrap: 'pretty' }}
                    >
                        AI-powered study plans, interactive quizzes, and parent visibility
                        — so your child learns more and scrolls less.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <a
                            href="https://student.learnforest.app"
                            data-ga-cta="student_signin"
                            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3 rounded-xl text-lg transition-colors text-center"
                        >
                            Student sign-in
                        </a>
                        <a
                            href="https://parent.learnforest.app/"
                            data-ga-cta="parent_signin"
                            className="bg-white border-2 border-darkrock/50 hover:border-darkrock/80 text-darkrock font-semibold px-8 py-3 rounded-xl text-lg transition-colors text-center"
                        >
                            Parent sign-in
                        </a>
                    </div>
                </div>
            </div>
        ) : (
            <div className="flex justify-between">
                <div className="flex w-1/3 sm:w-1/4 lg:w-1/5 items-end">
                    <img src="/bg-sky-top-left.jpeg" alt="" className="w-full" />
                </div>
                <div className="z-10 w-1/3 sm:w-1/2 lg:w-3/5 flex items-center justify-center">
                    {username && <div className="flex justify-center gap-4">
                        {
                        headerData && headerData.map((childProfile, index) => (
                            <div key={childProfile.id} className="text-center cursor-pointer"
                                onClick={() => onChildProfile(childProfile.username)}>
                                <div
                                    ref={(element) => { bubbleRefs.current[index] = element; }}
                                    className={`w-24 h-24 flex items-center justify-center mx-auto ${shouldAnimateBubbles ? 'bubble-physics-enter' : ''}`}>
                                    <div className={`w-[88px] h-[88px] text-xl rounded-full ${childProfile.username === username ? bubbleColors[index % bubbleColors.length] : 'bg-white'} ring-[1.5px] ring-offset-[6px] ring-offset-white ${getStyle(childProfile.username)} flex items-center justify-center`}>
                                        {getProfilePicture(childProfile, '🐠')}
                                    </div>
                                </div>
                                <div className="mt-2">
                                    {childProfile.first_name}
                                </div>
                            </div>
                        ))
                        }
                        {
                            !hasSubscriptionAction() &&
                            <div className="text-center cursor-pointer"
                                onClick={onAddChildProfile}>
                                <div
                                    ref={(element) => { bubbleRefs.current[headerData?.length || 0] = element; }}
                                    className={`w-24 h-24 flex items-center justify-center mx-auto ${shouldAnimateBubbles ? 'bubble-physics-enter' : ''}`}>
                                    <div className={`w-[88px] h-[88px] rounded-full bg-white ring-[1.5px] ring-transparent ring-offset-[6px] ring-offset-white flex items-center justify-center`}>
                                        ＋
                                    </div>
                                </div>
                                <div className="mt-2">
                                    Add
                                </div>
                            </div>
                        }
                    </div>}
                </div>
                <div className="ml-auto flex w-1/3 sm:w-1/4 lg:w-1/5 items-end">
                    <img src="/bg-sky-top-right.jpeg" alt="" className="w-full" />
                </div>
            </div>
        )}
    </nav>;
}

export default Header;
